/*
 * Delivery dates use the fixed PostNL holiday calendar, not a live carrier API.
 * Source: https://www.postnl.nl/zakelijk/klantenservice/versturen-bezorgen/aangepaste-bezorgdagen-vertragingen/feestdagen/
 * 2026 schedule: https://www.postnl.nl/api/assets/blt43aa441bfc1e29f2/blt97142f4f9abdd035/698a0220568487439f44a511/DEF3_binnenland_zakelijk_standaard_en_vast_2026.pdf
 * Good Friday and Liberation Day are not closed by default in this schedule;
 * add exceptional dates in the section setting. This remains an estimate and
 * is not a future carrier-policy guarantee.
 */
(function (root) {
  'use strict';

  const TIME_ZONE = 'Europe/Amsterdam';
  const DUTCH_WEEKDAYS = ['zondag', 'maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag'];
  const dateFormatter = typeof Intl !== 'undefined'
    ? new Intl.DateTimeFormat('en-CA', {
        timeZone: TIME_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      })
    : null;

  function isoDate(year, month, day) {
    return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  function parseIsoDate(value) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return null;
    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day ? date : null;
  }

  function addDays(value, days) {
    const date = parseIsoDate(value);
    if (!date) return null;
    date.setUTCDate(date.getUTCDate() + days);
    return isoDate(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
  }

  function dayOfWeek(value) {
    const date = parseIsoDate(value);
    return date ? date.getUTCDay() : null;
  }

  function easterSunday(year) {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return isoDate(year, month, day);
  }

  function fixedClosures(year) {
    const easter = easterSunday(year);
    const kingsDay = dayOfWeek(isoDate(year, 4, 27)) === 0 ? isoDate(year, 4, 26) : isoDate(year, 4, 27);
    return new Set([
      isoDate(year, 1, 1),
      kingsDay,
      addDays(easter, 1),
      addDays(easter, 39),
      addDays(easter, 49),
      addDays(easter, 50),
      isoDate(year, 12, 25),
      isoDate(year, 12, 26),
    ]);
  }

  function parseExtraClosures(value) {
    const closures = new Set();
    String(value || '').split(/[\s,;]+/).forEach((candidate) => {
      if (parseIsoDate(candidate)) closures.add(candidate);
    });
    return closures;
  }

  function isDeliveryDay(value, extraClosures) {
    const weekday = dayOfWeek(value);
    if (weekday === null || weekday === 0 || weekday === 6) return false;
    const year = Number(value.slice(0, 4));
    return !fixedClosures(year).has(value) && !(extraClosures && extraClosures.has(value));
  }

  function getAmsterdamParts(now) {
    const date = now instanceof Date ? now : new Date(now);
    if (!dateFormatter || Number.isNaN(date.getTime())) return null;
    const parts = Object.fromEntries(dateFormatter.formatToParts(date).filter((part) => part.type !== 'literal').map((part) => [part.type, part.value]));
    return {
      date: isoDate(Number(parts.year), Number(parts.month), Number(parts.day)),
      hour: Number(parts.hour),
      minute: Number(parts.minute),
    };
  }

  function nextDelivery(now, extraClosures) {
    const local = getAmsterdamParts(now);
    if (!local) return null;
    // 23:59 starts the next processing day; 23:58 is still before cut-off.
    const afterCutoff = local.hour > 23 || (local.hour === 23 && local.minute >= 59);
    let candidate = addDays(local.date, afterCutoff ? 2 : 1);
    const closures = extraClosures instanceof Set ? extraClosures : parseExtraClosures(extraClosures);
    for (let attempts = 0; candidate && attempts < 370; attempts += 1) {
      if (isDeliveryDay(candidate, closures)) return candidate;
      candidate = addDays(candidate, 1);
    }
    return null;
  }

  function deliveryDetails(now, extraClosures) {
    const date = nextDelivery(now, extraClosures);
    if (!date) return null;
    return { date, weekday: DUTCH_WEEKDAYS[dayOfWeek(date)] };
  }

  function renderStatus(element) {
    if (!element || element.dataset.deliveryNl !== 'true') return;
    const delivery = deliveryDetails(new Date(), element.dataset.extraClosures);
    if (!delivery || !root.document) return;
    const prefix = root.document.createTextNode('Op voorraad, ');
    const emphasis = root.document.createElement('strong');
    const time = root.document.createElement('time');
    time.dateTime = delivery.date;
    time.textContent = delivery.weekday;
    emphasis.append(time, root.document.createTextNode(' geleverd'));
    element.replaceChildren(prefix, emphasis);
  }

  const api = { TIME_ZONE, addDays, dayOfWeek, easterSunday, fixedClosures, parseExtraClosures, isDeliveryDay, getAmsterdamParts, nextDelivery, deliveryDetails };

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (!root || !root.document) return;
  if (root.__movitaminsDeliveryInitialized) return;
  root.__movitaminsDeliveryInitialized = true;
  root.MovitaminsDelivery = api;

  const statuses = new Set();
  let refreshTimer = null;
  function refresh() {
    statuses.forEach((element) => {
      if (!element.isConnected) statuses.delete(element);
      else renderStatus(element);
    });
    if (!statuses.size && refreshTimer) {
      root.clearInterval(refreshTimer);
      refreshTimer = null;
    }
  }
  function register(scope) {
    const elements = scope.matches?.('[data-movitamins-delivery]') ? [scope] : scope.querySelectorAll?.('[data-movitamins-delivery]');
    if (!elements) return;
    elements.forEach((element) => statuses.add(element));
    refresh();
    if (statuses.size && !refreshTimer) refreshTimer = root.setInterval(refresh, 60000);
  }
  function start() {
    register(root.document);
    root.document.addEventListener('shopify:section:load', (event) => register(event.target));
    root.document.addEventListener('visibilitychange', () => {
      if (!root.document.hidden) refresh();
    });
  }
  api.register = register;
  if (root.document.readyState === 'loading') root.document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})(typeof window !== 'undefined' ? window : globalThis);
